import React, { useEffect, useRef, useState } from "react";
import { Search, Plus, SlidersHorizontal } from "lucide-react";
import { usePermissions } from "../../lib/permissions";

/**
 * Barre d'outils harmonisée des modules de gestion (Réservations, Flotte, Clients, Devis).
 *
 * Desktop / tablette (lg+) — ordre de gauche à droite :
 *   [ Recherche + icône filtres (Gmail) ] — espace flexible — [ Vignettes/Liste ] [ … ] [ + Action ]
 * Le titre et le compteur ne sont visibles que sur mobile (sur desktop, l'onglet actif nomme le module
 * et porte son badge de compteur).
 *
 * Le panneau de filtres s'ouvre sous la recherche (popover aligné à droite sur desktop, inline sur mobile),
 * se ferme au clic extérieur ou via Échap, et réutilise les filtres existants passés via `filters`.
 *
 * Props :
 *  - title, count            : affichés sur mobile uniquement
 *  - search, onSearch        : champ de recherche (conservé tel quel par module)
 *  - filters                 : contenu du panneau de filtres (contrôles existants du module)
 *  - filtersActive           : nombre (ou booléen) de filtres actifs → pastille sur l'icône
 *  - onClearFilters          : callback optionnel « Effacer les filtres »
 *  - viewToggle              : élément <ViewToggle/> (null si le module n'a pas de vues)
 *  - extra                   : actions secondaires (menu …, Export/Import, liens spécifiques)
 *  - addLabel, onAdd         : bouton violet principal
 *  - permissionModule        : masque le bouton Ajouter sans la permission `create`
 */
export default function TabToolbar({
  title, count, search, onSearch, searchPlaceholder = "Rechercher…",
  filters = null, filtersActive = 0, onClearFilters = null,
  viewToggle = null, addLabel, onAdd, addTestid = "toolbar-add-btn", searchTestid = "toolbar-search",
  extra = null, permissionModule = null,
}) {
  const { can } = usePermissions();
  const showAdd = onAdd && (!permissionModule || can(permissionModule, "create"));
  const [open, setOpen] = useState(false);
  const searchRef = useRef(null);
  const activeCount = typeof filtersActive === "number" ? filtersActive : (filtersActive ? 1 : 0);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div className="relative flex flex-col lg:flex-row lg:items-center gap-3" data-testid="tab-toolbar">
      {/* Titre + compteur — mobile uniquement + cluster d'actions droite (fusionné dans la ligne desktop via lg:contents) */}
      <div className="flex items-center gap-2 lg:contents">
        {title && <h2 className="lg:hidden font-heading text-lg font-semibold text-slate-900 shrink-0">{title}</h2>}
        {count !== undefined && (
          <span className="lg:hidden px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-bold shrink-0" data-testid="toolbar-count">{count}</span>
        )}
        <div className="ml-auto lg:ml-0 lg:contents flex items-center gap-2">
          <div className="hidden lg:block lg:flex-1 lg:order-2" aria-hidden="true" />
          {viewToggle && <div className="shrink-0 lg:order-3">{viewToggle}</div>}
          {extra && <div className="flex items-center gap-2 shrink-0 lg:order-4">{extra}</div>}
          {showAdd && (
            <button
              onClick={onAdd}
              className="shrink-0 inline-flex items-center justify-center gap-1.5 h-10 px-3 sm:px-4 rounded-xl bg-violet-600 text-white text-xs font-bold hover:bg-violet-500 transition-colors lg:order-5"
              data-testid={addTestid}
            >
              <Plus className="h-4 w-4" strokeWidth={2.5} /> <span className="hidden sm:inline">{addLabel}</span>
            </button>
          )}
        </div>
      </div>

      {/* Recherche + icône filtres (ancre du panneau) */}
      {onSearch && (
        <div ref={searchRef} className="relative w-full lg:w-[320px] shrink-0 lg:order-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className={`w-full h-10 pl-9 ${filters ? "pr-11" : "pr-3"} rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500`}
              data-testid={searchTestid}
            />
            {filters && (
              <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-label="Filtres"
                title="Filtres"
                className={`absolute right-1.5 inset-y-0 my-auto inline-flex items-center justify-center h-7 w-8 rounded-lg transition-colors ${
                  activeCount > 0 ? "text-violet-700 bg-violet-50" : "text-slate-500 hover:bg-slate-100"
                }`}
                data-testid="toolbar-filter-btn"
              >
                <SlidersHorizontal className="h-4 w-4" />
                {activeCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-violet-600 text-white text-[9px] font-bold flex items-center justify-center" data-testid="toolbar-filter-count">{activeCount}</span>
                )}
              </button>
            )}
          </div>
          {open && filters && (
            <div
              className="z-40 mt-2 w-full lg:absolute lg:left-0 lg:top-full lg:mt-2 lg:w-max lg:min-w-[300px] lg:max-w-[min(760px,calc(100vw-7rem))] rounded-xl border border-slate-200 bg-white p-3 lg:shadow-xl"
              data-testid="toolbar-filters-panel"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Filtres</span>
                {onClearFilters && activeCount > 0 && (
                  <button onClick={onClearFilters} className="text-[11px] font-semibold text-violet-600 hover:underline" data-testid="toolbar-filters-clear">Effacer</button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">{filters}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
